import React, { useState, useEffect } from 'react';
import {
  AppBar, Box, IconButton, Badge, Avatar, Menu, MenuItem, Divider,
  Typography, Toolbar, Chip, Button, Popover, List, ListItem,
  ListItemText, CircularProgress, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, SelectChangeEvent
} from '@mui/material';
import {
  NotificationsOutlined, KeyboardArrowDownOutlined, LogoutOutlined,
  PersonOutlined, SettingsOutlined, SwapHorizOutlined, BusinessOutlined,
  MenuOutlined, MenuOpenOutlined, DoneAllOutlined,
  DarkModeOutlined, LightModeOutlined, AutoAwesomeOutlined, CloseOutlined
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import authApi from '../../api/authApi';
import notificationApi, { NotificationDto } from '../../api/notificationApi';
import { OrganisationContextDto } from '../../api/authApi';
import FavouritesMenu from './FavouritesMenu';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import { useAuthenticatedImageUrl } from '../../hooks/useAuthenticatedImageUrl';
import { formatLocalDateTime } from '../../utils/dateTime';

interface HeaderProps {
  onToggleSidebar?: () => void;
  sidebarCollapsed?: boolean;
  aiOpen?: boolean;
  onToggleAI?: () => void;
}

export const Header: React.FC<HeaderProps> = ({ onToggleSidebar, sidebarCollapsed, aiOpen, onToggleAI }) => {
  const navigate = useNavigate();
  const { user, selectedOrgId, selectedSubOrgId, logout, setContext } = useAuthStore();
  const { mode, toggleMode } = useThemeMode();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [switchOrgId, setSwitchOrgId] = useState('');
  const [switchSubOrgId, setSwitchSubOrgId] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifAnchorEl, setNotifAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<NotificationDto[]>([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const profileImageUrl = useAuthenticatedImageUrl(user?.profileImageUrl);

  useEffect(() => {
    notificationApi.getNotifications()
      .then(r => setUnreadCount(r.data?.data?.unreadCount || 0))
      .catch(() => {});
    const interval = setInterval(() => {
      notificationApi.getNotifications()
        .then(r => setUnreadCount(r.data?.data?.unreadCount || 0))
        .catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    try { await authApi.logout(); } catch {}
    logout();
    navigate('/login');
  };

  const handleOpenNotifications = (e: React.MouseEvent<HTMLElement>) => {
    setNotifAnchorEl(e.currentTarget);
    setNotifLoading(true);
    notificationApi.getNotifications()
      .then(r => {
        const data = r.data?.data;
        setNotifications(data?.items || []);
        setUnreadCount(data?.unreadCount || 0);
      })
      .catch(() => {})
      .finally(() => setNotifLoading(false));
  };

  const handleMarkAllRead = () => {
    notificationApi.markAllAsRead()
      .then(() => {
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        setUnreadCount(0);
      })
      .catch(() => {});
  };

  const handleNotifClick = (notif: NotificationDto) => {
    if (!notif.isRead) {
      notificationApi.markAsRead(notif.notificationId)
        .then(() => {
          setNotifications(prev => prev.map(n =>
            n.notificationId === notif.notificationId ? { ...n, isRead: true } : n
          ));
          setUnreadCount(prev => Math.max(0, prev - 1));
        })
        .catch(() => {});
    }
    if (notif.linkUrl) {
      navigate(notif.linkUrl);
      setNotifAnchorEl(null);
    }
  };

  const selectedOrg = user?.organisations?.find(o => o.organisationId === selectedOrgId);
  const selectedSubOrg = selectedOrg?.subOrganisations?.find(s => s.subOrganisationId === selectedSubOrgId);

  const hasMultipleContexts = (user?.organisations || []).some(
    org => org.canAccessOrganisationLevel && (org.subOrganisations?.length || 0) > 0
  ) || (user?.organisations?.length || 0) > 1 ||
    (user?.organisations?.length === 1 && (user.organisations[0].subOrganisations?.length || 0) > 1);

  const openSwitcher = () => {
    setSwitchOrgId(selectedOrgId || '');
    setSwitchSubOrgId(selectedSubOrgId || '');
    setSwitcherOpen(true);
    setAnchorEl(null);
  };

  const handleSwitchOrgChange = (e: SelectChangeEvent) => {
    const nextOrgId = e.target.value;
    const nextOrg = user?.organisations?.find(
      (o: OrganisationContextDto) => o.organisationId === nextOrgId
    );
    const nextSubOrgs = nextOrg?.subOrganisations || [];
    setSwitchOrgId(nextOrgId);
    setSwitchSubOrgId(!nextOrg?.canAccessOrganisationLevel && nextSubOrgs.length === 1 ? nextSubOrgs[0].subOrganisationId : '');
  };

  const handleSwitchConfirm = () => {
    if (!switcherCanAccessOrgLevel && switcherSubOrgs.length > 0 && !switchSubOrgId) return;
    setContext(switchOrgId || null, switchSubOrgId || null);
    window.location.reload();
  };

  const switcherOrg = user?.organisations?.find(
    (o: OrganisationContextDto) => o.organisationId === switchOrgId
  );
  const switcherSubOrgs = switcherOrg?.subOrganisations || [];
  const switcherCanAccessOrgLevel = Boolean(switcherOrg?.canAccessOrganisationLevel);

  return (
    <>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(9, 21, 36, 0.9)' : 'rgba(255,255,255,0.82)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid',
          borderColor: 'divider',
          zIndex: 1,
          height: 66,
          justifyContent: 'center',
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            px: { xs: 1.5, sm: 2 },
            minHeight: '66px !important',
            height: 66,
            gap: 1,
            alignItems: 'center',
          }}
        >

          {/* Sidebar toggle */}
          {onToggleSidebar && (
            <IconButton
              size="small"
              onClick={onToggleSidebar}
              sx={{
                color: 'text.secondary',
                borderRadius: 2,
                width: 34,
                height: 34,
                '&:hover': { color: 'primary.main', bgcolor: theme => theme.palette.mode === 'dark' ? '#13243a' : '#eef5ff' },
              }}
            >
              {sidebarCollapsed
                ? <MenuOutlined sx={{ fontSize: 20 }} />
                : <MenuOpenOutlined sx={{ fontSize: 20 }} />
              }
            </IconButton>
          )}

          <Box sx={{ flex: 1 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>

            {/* Current Context Chip */}
            {selectedOrg && (
              <Chip
                label={selectedSubOrg
                  ? `${selectedOrg.name} / ${selectedSubOrg.name}`
                  : selectedOrg.name
                }
                size="small"
                onClick={hasMultipleContexts ? openSwitcher : undefined}
                icon={hasMultipleContexts
                  ? <SwapHorizOutlined sx={{ fontSize: '14px !important', color: 'text.secondary !important' }} />
                  : undefined
                }
                sx={{
                  bgcolor: theme => theme.palette.mode === 'dark' ? '#0a1626' : '#f7faff',
                  color: 'text.primary',
                  fontSize: '0.7rem',
                  height: 26,
                  maxWidth: { xs: 150, sm: 220, md: 300 },
                  borderRadius: 999,
                  border: '1px solid',
                  borderColor: 'divider',
                  cursor: hasMultipleContexts ? 'pointer' : 'default',
                  '&:hover': hasMultipleContexts ? { bgcolor: theme => theme.palette.mode === 'dark' ? '#13243a' : '#eef5ff', borderColor: '#aac3ee' } : {},
                  '& .MuiChip-label': { px: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                }}
              />
            )}

            {/* Favourites */}
            <FavouritesMenu />

            <Tooltip title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
              <IconButton
                size="small"
                onClick={toggleMode}
                sx={{
                  color: mode === 'dark' ? '#f5a416' : 'text.secondary',
                  borderRadius: 2,
                  width: 34,
                  height: 34,
                  '&:hover': { color: mode === 'dark' ? '#ffc95d' : 'primary.main', bgcolor: mode === 'dark' ? 'rgba(245,164,22,0.12)' : '#eef5ff' },
                }}
              >
                {mode === 'dark' ? <LightModeOutlined sx={{ fontSize: 20 }} /> : <DarkModeOutlined sx={{ fontSize: 20 }} />}
              </IconButton>
            </Tooltip>

            {/* Notifications */}
            <IconButton
              size="small"
              onClick={handleOpenNotifications}
              sx={{
                color: 'text.secondary',
                borderRadius: 2,
                width: 34,
                height: 34,
                '&:hover': { color: 'primary.main', bgcolor: theme => theme.palette.mode === 'dark' ? '#13243a' : '#eef5ff' },
              }}
            >
              <Badge badgeContent={unreadCount || 0} color="error" max={99}>
                <NotificationsOutlined sx={{ fontSize: 20 }} />
              </Badge>
            </IconButton>

            <Popover
              open={Boolean(notifAnchorEl)}
              anchorEl={notifAnchorEl}
              onClose={() => setNotifAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: {
                  mt: 1,
                  width: 340,
                  maxHeight: 420,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  display: 'flex',
                  flexDirection: 'column',
                }
              }}
            >
              <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" fontWeight={600}>Notifications</Typography>
                {unreadCount > 0 && (
                  <IconButton size="small" onClick={handleMarkAllRead} title="Mark all as read">
                    <DoneAllOutlined sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Box>
              <Box sx={{ overflowY: 'auto', flex: 1 }}>
                {notifLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : notifications.length === 0 ? (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <NotificationsOutlined sx={{ fontSize: 32, color: 'divider', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">No notifications</Typography>
                  </Box>
                ) : (
                  <List disablePadding>
                    {notifications.map((notif, idx) => (
                      <React.Fragment key={notif.notificationId}>
                        {idx > 0 && <Divider sx={{ borderColor: 'action.hover' }} />}
                        <ListItem
                          onClick={() => handleNotifClick(notif)}
                          sx={{
                            px: 2,
                            py: 1,
                            cursor: notif.linkUrl ? 'pointer' : 'default',
                            bgcolor: notif.isRead ? 'transparent' : theme => theme.palette.mode === 'dark' ? '#102642' : '#edf7fb',
                            '&:hover': { bgcolor: 'action.hover' },
                            alignItems: 'flex-start',
                          }}
                        >
                          {!notif.isRead && (
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main', mt: 0.75, mr: 1, flexShrink: 0 }} />
                          )}
                          <ListItemText
                            primary={
                              <Typography variant="body2" fontWeight={notif.isRead ? 400 : 600} sx={{ fontSize: '0.8rem' }}>
                                {notif.title}
                              </Typography>
                            }
                            secondary={
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  {notif.message}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                                  {formatLocalDateTime(notif.createdAt)}
                                </Typography>
                              </Box>
                            }
                            sx={{ m: 0 }}
                          />
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Box>
            </Popover>

            {/* User Menu Button */}
            <Button
              size="small"
              endIcon={<KeyboardArrowDownOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />}
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                color: 'text.primary',
                textTransform: 'none',
                minWidth: 0,
                gap: 0.75,
                px: 1,
                py: 0.5,
                borderRadius: 2,
                '&:hover': { bgcolor: theme => theme.palette.mode === 'dark' ? '#13243a' : '#eef5ff' },
              }}
            >
              <Avatar
                src={profileImageUrl}
                sx={{
                  width: 28,
                  height: 28,
                  bgcolor: theme => theme.palette.mode === 'dark' ? '#2452c7' : '#071b3a',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  color: '#ffffff',
                }}
              >
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </Avatar>
            </Button>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 210,
                  bgcolor: theme => theme.palette.mode === 'dark' ? '#0a1626' : 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  borderRadius: 2,
                  color: 'text.primary',
                }
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary' }}>
                  {user?.firstName} {user?.lastName}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{user?.email}</Typography>
              </Box>
              <Divider sx={{ borderColor: 'divider' }} />
              {hasMultipleContexts && (
                <MenuItem
                  onClick={openSwitcher}
                  sx={{ gap: 1.5, py: 1, color: 'text.primary', '&:hover': { bgcolor: 'action.hover' } }}
                >
                  <SwapHorizOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2">Switch Context</Typography>
                </MenuItem>
              )}
              <MenuItem
                onClick={() => { navigate('/profile'); setAnchorEl(null); }}
                sx={{ gap: 1.5, py: 1, color: 'text.primary', '&:hover': { bgcolor: 'action.hover' } }}
              >
                <PersonOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2">Profile</Typography>
              </MenuItem>
              <MenuItem
                onClick={() => { navigate('/settings'); setAnchorEl(null); }}
                sx={{ gap: 1.5, py: 1, color: 'text.primary', '&:hover': { bgcolor: 'action.hover' } }}
              >
                <SettingsOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2">Settings</Typography>
              </MenuItem>
              <Divider sx={{ borderColor: 'divider' }} />
              <MenuItem
                onClick={handleLogout}
                sx={{ gap: 1.5, py: 1, color: '#c62828', '&:hover': { bgcolor: 'action.hover' } }}
              >
                <LogoutOutlined sx={{ fontSize: 18 }} />
                <Typography variant="body2">Sign out</Typography>
              </MenuItem>
            </Menu>

            {/* AI Assistant */}
            {onToggleAI && (
              <Tooltip title={aiOpen ? 'Close AI Assistant' : 'AI Assistant'}>
                <Button
                  size="small"
                  variant="contained"
                  aria-label={aiOpen ? 'Close AI Assistant' : 'Open AI Assistant'}
                  onClick={onToggleAI}
                  startIcon={<AutoAwesomeOutlined sx={{ fontSize: '18px !important' }} />}
                  sx={{
                    minWidth: { xs: 42, sm: 126 },
                    height: 38,
                    px: { xs: 1.1, sm: 1.5 },
                    color: '#ffffff',
                    bgcolor: aiOpen ? 'primary.dark' : '#2452c7',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 900,
                    fontSize: '0.78rem',
                    letterSpacing: 0,
                    border: '1px solid rgba(255,255,255,0.28)',
                    boxShadow: aiOpen
                      ? '0 0 0 3px rgba(36,82,199,0.18), 0 8px 18px rgba(36,82,199,0.28)'
                      : '0 8px 18px rgba(36,82,199,0.24)',
                    '& .MuiButton-startIcon': { m: { xs: 0, sm: '0 6px 0 0' } },
                    '&:hover': {
                      bgcolor: 'primary.dark',
                      boxShadow: '0 0 0 4px rgba(36,82,199,0.18), 0 10px 22px rgba(36,82,199,0.3)',
                    },
                    '&:focus-visible': {
                      outline: '3px solid rgba(245,164,22,0.6)',
                      outlineOffset: 2,
                    },
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    AI Assistant
                  </Box>
                </Button>
              </Tooltip>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Context Switcher Dialog */}
      <Dialog
        open={switcherOpen}
        onClose={() => setSwitcherOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            color: 'text.primary',
            boxShadow: theme => theme.palette.mode === 'dark'
              ? '0 24px 60px rgba(0,0,0,0.45)'
              : '0 24px 60px rgba(15,23,42,0.22)',
          }
        }}
        BackdropProps={{
          sx: {
            bgcolor: theme => theme.palette.mode === 'dark'
              ? 'rgba(3, 9, 18, 0.58)'
              : 'rgba(15, 23, 42, 0.38)',
            backdropFilter: 'blur(2px)',
          }
        }}
      >
        <DialogTitle sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 1,
                display: 'grid',
                placeItems: 'center',
                color: 'primary.main',
                bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(36,82,199,0.2)' : '#eef5ff',
                flexShrink: 0,
              }}
            >
              <BusinessOutlined sx={{ fontSize: 19 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={900} sx={{ color: 'text.primary', lineHeight: 1.2 }}>
                Switch Context
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.35 }}>
                Choose the organisation and sub-organisation for this workspace.
              </Typography>
            </Box>
            <IconButton
              size="small"
              aria-label="Close context switcher"
              onClick={() => setSwitcherOpen(false)}
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1,
                color: 'text.secondary',
                '&:hover': { color: 'primary.main', bgcolor: 'action.hover' },
              }}
            >
              <CloseOutlined sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, py: 2.5, bgcolor: theme => theme.palette.mode === 'dark' ? '#081525' : '#f8fbff' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth size="small" sx={{ mt: 1 }}>
              <InputLabel id="switch-org-label">Organisation</InputLabel>
              <Select
                value={switchOrgId}
                label="Organisation"
                labelId="switch-org-label"
                onChange={handleSwitchOrgChange}
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                }}
              >
                {(user?.organisations || []).map((org: OrganisationContextDto) => (
                  <MenuItem key={org.organisationId} value={org.organisationId}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {switchOrgId && switcherSubOrgs.length > 0 && (
              <FormControl fullWidth size="small">
                <InputLabel id="switch-suborg-label">{switcherCanAccessOrgLevel ? 'Sub-Organisation (optional)' : 'Sub-Organisation'}</InputLabel>
                <Select
                  value={switchSubOrgId}
                  label={switcherCanAccessOrgLevel ? 'Sub-Organisation (optional)' : 'Sub-Organisation'}
                  labelId="switch-suborg-label"
                  onChange={(e) => setSwitchSubOrgId(e.target.value)}
                  sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  {switcherCanAccessOrgLevel && (
                    <MenuItem value="">
                      <em>Organisation level (all sub-orgs)</em>
                    </MenuItem>
                  )}
                  {switcherSubOrgs.map((sub: any) => (
                    <MenuItem key={sub.subOrganisationId} value={sub.subOrganisationId}>
                      {sub.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.75, gap: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            onClick={() => setSwitcherOpen(false)}
            variant="outlined"
            color="inherit"
            size="small"
            sx={{ borderRadius: 1, fontWeight: 800, minHeight: 36, px: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSwitchConfirm}
            variant="contained"
            size="small"
            disabled={!switchOrgId || (!switcherCanAccessOrgLevel && switcherSubOrgs.length > 0 && !switchSubOrgId)}
            sx={{
              borderRadius: 1,
              fontWeight: 900,
              minHeight: 36,
              px: 2.25,
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
            }}
          >
            Switch
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Header;
